import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { IdentifierValue } from "@/components/ui/identifier-value";
import { Pagination } from "@/components/ui/pagination";
import { Table, TableWrapper } from "@/components/ui/table";
import { LoadingTable } from "@/components/ui/system-state";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { ReadOnlyBanner, snapshotFreshness } from "@/features/provider-migration/shared";
import { useProviderMigrationInventory } from "@/hooks/use-provider-migration";
import type { ApiProviderMigrationInventoryItem } from "@/lib/api";

const PAGE_SIZE = 25;
const FIELDS = ["Vault ID", "Asset ID", "Provider resource ID", "Total", "Available"];

// Do not use inventoryAttribute here: it caps strings at 256 characters.
function attribute(item: ApiProviderMigrationInventoryItem, key: string): string | null {
  const value = item.attributes?.[key];
  return typeof value === "string" && value.trim() ? value : null;
}

function amount(item: ApiProviderMigrationInventoryItem, key: string): string {
  const value = attribute(item, key);
  return value !== null && value === value.trim() && /^\d+(?:\.\d+)?$/.test(value) ? value : "Unavailable";
}

/** Mounted only by the open disclosure; source records never enter the Wallet model. */
export function ProviderWalletInventory() {
  const { data, loading, error, refresh } = useProviderMigrationInventory("ASSET_WALLETS");
  const [page, setPage] = useState(1);
  const snapshotId = typeof data?.snapshotId === "string" && data.snapshotId.trim() ? data.snapshotId : null;
  const unavailable = !loading && (error || !data || !snapshotId);
  const freshness = snapshotFreshness(data?.freshUntil ?? null);
  const pageCount = Math.max(1, Math.ceil((data?.items.length ?? 0) / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const retry = () => { setPage(1); refresh(); };

  return (
    <section aria-label="Sealed provider asset wallets" className="min-w-0 space-y-4">
      <ReadOnlyBanner />
      <p className="text-sm text-muted">Origin — sealed provider inventory, separate from operational Alloy wallets. Source amounts are provider strings and are excluded from wallet counts and Total AUM.</p>
      <Button variant="secondary" size="sm" onClick={retry} disabled={loading}>Retry provider inventory</Button>
      {loading ? <LoadingTable rows={3} cols={5} label="Loading provider inventory" /> : unavailable ? (
        <div role="status" className="text-sm text-muted">
          Provider inventory unavailable — {error ? "the gateway refused or could not complete the read." : "no sealed snapshot ID was returned."}
          {error && <SupportDetails reference={error.supportReference} capability={error.capability} />}
        </div>
      ) : data && snapshotId ? (
        <>
          <div className="flex min-w-0 flex-wrap items-center gap-3 text-sm">
            <span className="min-w-0">Snapshot <IdentifierValue value={snapshotId} /></span>
            <Badge variant={freshness === "fresh" ? "success" : freshness === "stale" ? "warning" : "neutral"}>
              {freshness === "fresh" ? "Fresh" : freshness === "stale" ? "Stale" : "Unknown"}
            </Badge>
          </div>
          {data.truncated && <p role="status" className="text-sm text-muted">Partial coverage — the bounded read did not retrieve the complete source inventory.</p>}
          {freshness !== "fresh" && <p className="text-sm text-muted">{freshness === "stale" ? "Snapshot is stale" : "Snapshot freshness is unknown"} — current source coverage cannot be confirmed.</p>}
          {data.items.length === 0 ? (
            <p role="status" className="text-sm text-muted">{!data.truncated && freshness === "fresh"
              ? "No source asset wallets in this complete fresh snapshot."
              : "No source rows returned — an empty source cannot be confirmed from this read."}</p>
          ) : (
            <div className="min-w-0 max-w-full">
              <TableWrapper className="min-w-0 max-w-full" tabIndex={0} role="region" aria-label="Provider asset wallet rows">
                <Table aria-label="Provider asset wallets">
                  <thead><tr>{FIELDS.map((field) => <th key={field} scope="col">{field}</th>)}</tr></thead>
                  <tbody>{data.items.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((item) => (
                    <tr key={`${data.category}:${item.providerResourceId}`}>
                      <td data-label="Vault ID"><IdentifierValue value={attribute(item, "vaultAccountId")} emptyFallback="Unavailable" /></td>
                      <td data-label="Asset ID"><IdentifierValue value={attribute(item, "assetId")} emptyFallback="Unavailable" /></td>
                      <td data-label="Provider resource ID"><IdentifierValue value={item.providerResourceId} emptyFallback="Unavailable" /></td>
                      <td data-label="Total"><span className="break-all font-mono">{amount(item, "total")}</span></td>
                      <td data-label="Available"><span className="break-all font-mono">{amount(item, "available")}</span></td>
                    </tr>
                  ))}</tbody>
                </Table>
              </TableWrapper>
              <Pagination page={currentPage} pageCount={pageCount} onPageChange={setPage} totalItems={data.items.length} pageSize={PAGE_SIZE} />
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}
